import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import { getUsers } from '../../api'
import './RoleConfiguration.css'

// Mirror of security/permissions.py — kept in sync manually
const ROLES = [
  { id: 'admin', label: 'Admin', description: 'Full platform access. Manages users, settings, all customers.' },
  { id: 'consultant', label: 'Consultant', description: 'Full access to assigned customers. Upload data, run analysis, create threads, generate exports.' },
  { id: 'business_development', label: 'Business Dev', description: 'Read-only access to assigned customers. Can chat and view findings, but cannot modify data.' },
]

const PERMISSION_GROUPS = [
  {
    group: 'Chat & Analysis',
    permissions: [
      { id: 'chat', label: 'Chat' },
      { id: 'chat_without_customer', label: 'Research Mode' },
    ],
  },
  {
    group: 'Data Management',
    permissions: [
      { id: 'upload_data', label: 'Upload Data' },
      { id: 'upload_documents', label: 'Upload Documents' },
      { id: 'delete_data', label: 'Delete Data' },
      { id: 'manage_connections', label: 'Manage Connectors' },
      { id: 'run_syncs', label: 'Run Syncs' },
      { id: 'run_checks', label: 'Run Checks' },
      { id: 'vendor_debug', label: 'Vendor Debug' },
      { id: 'manage_sync_schedules', label: 'Manage Sync Schedules' },
    ],
  },
  {
    group: 'Customer Context',
    permissions: [
      { id: 'view_customer', label: 'View Customer' },
      { id: 'edit_customer', label: 'Edit Customer' },
      { id: 'create_customer', label: 'Create Customer' },
      { id: 'delete_customer', label: 'Delete Customer' },
    ],
  },
  {
    group: 'Threads & Workflows',
    permissions: [
      { id: 'view_threads', label: 'View Threads' },
      { id: 'create_threads', label: 'Create Threads' },
      { id: 'edit_threads', label: 'Edit Threads' },
      { id: 'delete_threads', label: 'Delete Threads' },
    ],
  },
  {
    group: 'Exports & Reports',
    permissions: [
      { id: 'view_exports', label: 'View Exports' },
      { id: 'generate_exports', label: 'Generate Exports' },
    ],
  },
  {
    group: 'Findings',
    permissions: [
      { id: 'view_findings', label: 'View Findings' },
      { id: 'edit_findings', label: 'Edit Findings' },
      { id: 'respond_to_findings', label: 'Respond to Findings' },
      { id: 'view_employee_detail', label: 'View Employee Lifeline' },
    ],
  },
  {
    group: 'Knowledge Base',
    permissions: [
      { id: 'view_templates', label: 'View Templates' },
      { id: 'manage_templates', label: 'Manage Templates' },
      { id: 'view_knowledge', label: 'View Knowledge' },
      { id: 'manage_knowledge', label: 'Manage Knowledge' },
    ],
  },
  {
    group: 'Administration',
    permissions: [
      { id: 'manage_users', label: 'Manage Users' },
      { id: 'view_audit_log', label: 'View Audit Log' },
      { id: 'manage_platform', label: 'Manage Platform' },
      { id: 'view_all_customers', label: 'View All Customers' },
    ],
  },
]

// Which permissions each role has (mirrors security/permissions.py)
const ROLE_PERMISSIONS = {
  admin: 'all', // All permissions
  consultant: new Set([
    'chat', 'chat_without_customer', 'upload_data', 'upload_documents', 'delete_data',
    'manage_connections', 'view_customer', 'edit_customer', 'create_customer',
    'view_threads', 'create_threads', 'edit_threads', 'delete_threads',
    'view_exports', 'generate_exports', 'view_findings', 'edit_findings',
    'respond_to_findings', 'view_employee_detail', 'view_templates', 'view_knowledge',
  ]),
  business_development: new Set([
    'chat', 'chat_without_customer', 'view_customer', 'view_threads',
    'view_exports', 'view_findings', 'view_employee_detail', 'view_templates', 'view_knowledge',
  ]),
}

function hasPermission(role, permId) {
  if (ROLE_PERMISSIONS[role] === 'all') return true
  return ROLE_PERMISSIONS[role]?.has(permId) || false
}

const ROLE_COLORS = {
  admin: { bg: '#ede9fe', color: '#7c3aed', dot: '#7c3aed' },
  consultant: { bg: '#dbeefd', color: '#020c30', dot: '#020c30' },
  business_development: { bg: '#e0e7f8', color: '#2d5bc0', dot: '#2d5bc0' },
}

export default function RoleConfiguration() {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('matrix') // matrix | users

  useEffect(() => {
    getUsers().then(u => {
      setUsers(Array.isArray(u) ? u : [])
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const breadcrumbs = [
    { label: 'Admin', href: '/admin' },
    { label: 'Roles' },
  ]

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs}
        actions={<>
          <Link to="/admin/security" className="nav-btn">Security</Link>
          <Link to="/admin" className="nav-btn">Dashboard</Link>
        </>}
      />
      <div className="rc-page">
        {/* Role Summary Cards */}
        <div className="rc-role-cards">
          {ROLES.map(r => {
            const count = users.filter(u => u.role === r.id && u.is_active).length
            const colors = ROLE_COLORS[r.id]
            return (
              <div key={r.id} className="rc-role-card" style={{ borderLeftColor: colors.dot }}>
                <div className="rc-role-header">
                  <span className="rc-role-badge" style={{ background: colors.bg, color: colors.color }}>
                    {r.label}
                  </span>
                  <span className="rc-role-count">{count} user{count !== 1 ? 's' : ''}</span>
                </div>
                <div className="rc-role-desc">{r.description}</div>
                <div className="rc-role-perm-count">
                  {r.id === 'admin'
                    ? 'All permissions'
                    : `${ROLE_PERMISSIONS[r.id]?.size || 0} permissions`
                  }
                </div>
              </div>
            )
          })}
        </div>

        {/* Tab Switcher */}
        <div className="rc-tabs">
          <button className={`rc-tab ${activeTab === 'matrix' ? 'active' : ''}`}
            onClick={() => setActiveTab('matrix')}>Permission Matrix</button>
          <button className={`rc-tab ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}>User Assignments</button>
        </div>

        {activeTab === 'matrix' && (
          <div className="rc-matrix-wrap">
            <table className="rc-matrix">
              <thead>
                <tr>
                  <th className="rc-matrix-corner">Permission</th>
                  {ROLES.map(r => (
                    <th key={r.id} className="rc-matrix-role-header">
                      <span className="rc-role-dot" style={{ background: ROLE_COLORS[r.id].dot }} />
                      {r.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PERMISSION_GROUPS.map(g => (
                  <>
                    <tr key={g.group} className="rc-matrix-group-row">
                      <td colSpan={ROLES.length + 1} className="rc-matrix-group">{g.group}</td>
                    </tr>
                    {g.permissions.map(p => (
                      <tr key={p.id}>
                        <td className="rc-matrix-perm">{p.label}</td>
                        {ROLES.map(r => (
                          <td key={r.id} className="rc-matrix-cell">
                            {hasPermission(r.id, p.id)
                              ? <span className="rc-check" style={{ color: ROLE_COLORS[r.id].color }}>✓</span>
                              : <span className="rc-dash">—</span>
                            }
                          </td>
                        ))}
                      </tr>
                    ))}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="rc-users-wrap">
            {loading ? (
              <div className="rc-loading">Loading users…</div>
            ) : (
              <table className="rc-users-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Current Role</th>
                    <th>Change Role</th>
                    <th>Customers</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => u.is_active).map(u => (
                    <tr key={u.id}>
                      <td>
                        <div className="rc-user-cell">
                          <div className="rc-user-name">{u.display_name}</div>
                          <div className="rc-user-email">{u.email}</div>
                        </div>
                      </td>
                      <td>
                        <span className="rc-role-badge" style={{
                          background: ROLE_COLORS[u.role]?.bg || '#f3f4f6',
                          color: ROLE_COLORS[u.role]?.color || '#6b7280',
                        }}>
                          {ROLES.find(r => r.id === u.role)?.label || u.role}
                        </span>
                      </td>
                      <td>
                        {/* Roles change on the Security (users) screen only: it offers just the row's own
                            kind (person or machine) and confirms before saving. */}
                        <Link to="/admin/security">Change on the Security screen</Link>
                      </td>
                      <td>
                        <span className="rc-customer-count">{u.customer_ids?.length || 0} assigned</span>
                      </td>
                    </tr>
                  ))}
                  {users.filter(u => u.is_active).length === 0 && (
                    <tr><td colSpan={4} className="rc-empty">No active users</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </>
  )
}
